import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, LogIn, Loader2 } from "lucide-react";

const WORDMARK_URL = "https://customer-assets.emergentagent.com/job_a9be1f67-2b09-4dec-b876-bd68a994a515/artifacts/2ljnfsch_Enable%20Healthcare%20emblem_wordmark_BLUE.png";

export default function Login() {
  const navigate = useNavigate();
  const { login, verifyMfa } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // MFA challenge state — shown when the server returns mfa_required=true.
  const [mfaChallenge, setMfaChallenge] = useState(null); // { challenge_token, mfa_enrolled }
  const [mfaCode, setMfaCode] = useState("");

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfa_required) {
        setMfaChallenge({ challenge_token: res.challenge_token, mfa_enrolled: res.mfa_enrolled });
        setLoading(false);
        return;
      }
      navigate("/");
    } catch (err) {
      // Iter 182 (Feb 2026) — surface brute-force defence responses so
      // the user understands why we're refusing the login instead of
      // showing the generic "Invalid email or password" toast. Codes
      // come from /api/auth/login: ip_blocked, ip_rate_limited,
      // rate_limited, account_locked.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = (detail && typeof detail === "object") ? detail.code : null;
      const retry = (detail && typeof detail === "object") ? detail.retry_after : null;
      if (status === 429 && code === "ip_blocked") {
        const mins = Math.max(1, Math.round((retry || 3600) / 60));
        toast.error(`Too many failed attempts from your network. Locked for ~${mins} min. Contact an admin if this is wrong.`, { duration: 8000 });
      } else if (status === 429 && (code === "ip_rate_limited" || code === "rate_limited")) {
        toast.error(`Too many login attempts. Try again in ${retry || 60}s.`, { duration: 5000 });
      } else if (status === 423 && code === "account_locked") {
        const mins = Math.max(1, Math.round((retry || 900) / 60));
        toast.error(`Account locked after repeated failures. Try again in ~${mins} min.`, { duration: 6000 });
      } else if (status === 403) {
        toast.error("Account is disabled. Contact an admin.");
      } else {
        toast.error("Invalid email or password.");
      }
    } finally { setLoading(false); }
  };

  const handleMfaSubmit = async (e) => {
    e?.preventDefault();
    if (!mfaCode) { toast.error("Enter your 6-digit code"); return; }
    setLoading(true);
    try {
      await verifyMfa({ challenge_token: mfaChallenge.challenge_token, code: mfaCode });
      navigate("/");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invalid MFA code");
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--page-bg)", fontFamily: "var(--font)"
    }}>
      <div style={{ width: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src={WORDMARK_URL} alt="Enable Healthcare" style={{ height: 48, objectFit: "contain", margin: "0 auto 10px", display: "block" }} />
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", fontWeight: 500, letterSpacing: 0.2 }}>Customer Relationship Management Login</div>
        </div>

        {/* Login Card */}
        <div style={{ background: "var(--surface)", borderRadius: 14, border: "0.5px solid var(--border)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", padding: "28px 32px" }}
          data-testid="login-card">
          {!mfaChallenge ? (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
                Email address <span style={{ color: "var(--text-tertiary)", fontWeight: 500 }}>(EHI EMAIL)</span>
              </label>
              <div style={{ position: "relative" }}>
                <Mail style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "var(--text-tertiary)" }} />
                <input className="input" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" style={{ paddingLeft: 32, fontSize: 13 }} data-testid="login-email" />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Password</label>
              <div style={{ position: "relative" }}>
                <Lock style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "var(--text-tertiary)" }} />
                <input className="input" type={showPassword ? "text" : "password"} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Enter password"
                  style={{ paddingLeft: 32, paddingRight: 32, fontSize: 13 }} data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}>
                  {showPassword ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
                </button>
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading}
              style={{ width: "100%", padding: "10px", fontSize: 13, marginBottom: 10, justifyContent: "center" }} data-testid="login-submit">
              {loading ? <Loader2 style={{ width: 14, height: 14, animation: "spin 0.8s linear infinite" }} /> : <><LogIn style={{ width: 14, height: 14 }} /> Sign in</>}
            </button>
          </form>
          ) : (
            <form data-testid="mfa-challenge-form" onSubmit={handleMfaSubmit}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Two-factor verification</div>
                <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                  {mfaChallenge.mfa_enrolled
                    ? "Enter the 6-digit code from your authenticator app (or an 11-char backup code)."
                    : "This account requires MFA setup. Log in once you've completed enrollment."}
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <input
                  className="input"
                  data-testid="mfa-challenge-code"
                  autoFocus
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value)}
                  placeholder="000000 or BACKUP-CODE0"
                  style={{ padding: 10, fontSize: 16, textAlign: "center", letterSpacing: 3 }}
                />
              </div>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={loading}
                data-testid="mfa-challenge-submit"
                style={{ width: "100%", padding: "10px", fontSize: 13, marginBottom: 10, justifyContent: "center" }}
              >
                {loading ? <Loader2 style={{ width: 14, height: 14, animation: "spin 0.8s linear infinite" }} /> : "Verify & continue"}
              </button>
              <button
                type="button"
                onClick={() => { setMfaChallenge(null); setMfaCode(""); }}
                className="btn"
                style={{ width: "100%", padding: "8px", fontSize: 12, justifyContent: "center" }}
              >
                ← Use a different account
              </button>
            </form>
          )}
          {!mfaChallenge && (
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <button onClick={() => navigate("/forgot-password")} data-testid="login-forgot-password-btn" style={{ background: "none", border: "none", color: "var(--blue)", fontSize: 11, cursor: "pointer" }}>
                Forgot password?
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
